import React from "react";

import {
  Bike,
  Car,
  ChevronDown,
  IndianRupee,
  Loader2,
  Save,
  Trash2,
  Truck,
} from "lucide-react";

// =====================================================
// VEHICLE IMAGES
// =====================================================

const VEHICLE_IMAGES = {
  "2 Wheeler": "https://www.packky.com/images/Vechiles/two_wheeler.webp",
  "Two Wheeler": "https://www.packky.com/images/Vechiles/two_wheeler.webp",

  "3 Wheeler": "https://www.packky.com/images/Vechiles/three_wheeler.webp",
  "Three Wheeler": "https://www.packky.com/images/Vechiles/three_wheeler.webp",

  "4 Wheeler": "https://www.packky.com/images/Vechiles/four_wheeler.webp",
  "Four Wheeler": "https://www.packky.com/images/Vechiles/four_wheeler.webp",

  "Mini Truck": "https://www.packky.com/images/Vechiles/mini_truck.webp",
};

// =====================================================
// VEHICLE IMAGE
// =====================================================

const getVehicleImage = (name = "") => {
  const matchedKey = Object.keys(VEHICLE_IMAGES).find((key) =>
    name.toLowerCase().includes(key.toLowerCase()),
  );

  return matchedKey
    ? VEHICLE_IMAGES[matchedKey]
    : "https://www.packky.com/images/Vechiles/two_wheeler.webp";
};

// =====================================================
// VEHICLE ICON
// =====================================================

const getVehicleIcon = (name = "") => {
  const value = name.toLowerCase();

  if (value.includes("truck")) {
    return Truck;
  }

  if (value.includes("4") || value.includes("four")) {
    return Car;
  }

  if (value.includes("3") || value.includes("three")) {
    return Truck;
  }

  return Bike;
};

// =====================================================
// VEHICLE FARE LIST
// =====================================================

const VehicleFareList = ({
  vehicles = [],
  fares = [],

  loading,

  latestFareByVehicleId = {},
  distanceFareByVehicleId = {},

  fareForms = {},

  expandedVehicleId,

  fareSavingId,
  deletingId,
  deletingDistanceFareId,

  toggleVehicleFareTable,

  handleFareInput,
  handleSaveFare,

  handleDeleteVehicle,

  handleEditDistanceSlab,
  handleDeleteDistanceFare,

  formatAmount,
}) => {
  return (
    <div className="mt-6 flex-1 min-h-0 flex flex-col overflow-hidden">
      <section className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 bg-white">
        {/* =================================================
            HEADER
        ================================================= */}

        <div className="shrink-0 flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Vehicle Fare Configuration
            </h3>

            <p className="mt-0.5 text-xs text-gray-500">
              Set per km fare and minimum delivery charge for each vehicle.
            </p>
          </div>

          <div className="hidden text-xs text-gray-500 md:block">
            {vehicles.length} vehicles · {fares.length} fare rules
          </div>
        </div>

        {/* =================================================
            BODY
        ================================================= */}

        <div className="flex-1 min-h-0 overflow-auto">
          {/* =================================================
              LOADING
          ================================================= */}

          {loading ? (
            <div className="divide-y divide-gray-100">
              {Array.from({
                length: 4,
              }).map((_, index) => (
                <div
                  key={index}
                  className="grid animate-pulse grid-cols-1 gap-4 px-4 py-4 md:grid-cols-[260px_1fr_1fr_120px]"
                >
                  <div className="h-10 rounded bg-gray-100" />

                  <div className="h-10 rounded bg-gray-100" />

                  <div className="h-10 rounded bg-gray-100" />

                  <div className="h-10 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : vehicles.length === 0 ? (
            /* =================================================
                EMPTY
            ================================================= */

            <div className="px-4 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">
                No vehicle added yet
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Add vehicle first, then configure fare.
              </p>
            </div>
          ) : (
            /* =================================================
                VEHICLE LIST
            ================================================= */

            <div className="divide-y divide-gray-100">
              {vehicles.map((vehicle) => {
                const vehicleId = vehicle.pk_vehicle_id;

                const VehicleIcon = getVehicleIcon(vehicle.name);

                const currentFare = latestFareByVehicleId[String(vehicleId)];

                const form = fareForms[vehicleId] || {};

                const slabs = distanceFareByVehicleId[String(vehicleId)] || [];

                const isExpanded =
                  String(expandedVehicleId) === String(vehicleId);

                const isFareSaving = fareSavingId === vehicleId;

                const isDeleting = deletingId === vehicleId;

                return (
                  <React.Fragment key={vehicleId}>
                    {/* =================================================
                        VEHICLE FARE FORM
                    ================================================= */}

                    <div className="grid grid-cols-1 gap-4 px-4 py-4 transition hover:bg-gray-50 md:grid-cols-[280px_1fr_1fr_130px]">
                      {/* =================================================
                          VEHICLE INFO
                      ================================================= */}

                      <div className="flex items-center gap-3">
                        {/* EXPAND */}

                        <button
                          type="button"
                          onClick={() => toggleVehicleFareTable(vehicleId)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600"
                          title={
                            isExpanded
                              ? "Hide distance slabs"
                              : "Show distance slabs"
                          }
                        >
                          <ChevronDown
                            size={17}
                            className={`transition-transform duration-200 ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {/* IMAGE */}

                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50">
                          <img
                            src={getVehicleImage(vehicle.name)}
                            alt={vehicle.name}
                            className="h-10 w-10 object-contain"
                          />
                        </div>

                        {/* VEHICLE DETAILS */}

                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <VehicleIcon
                              size={14}
                              className="text-orange-500"
                            />

                            <p className="truncate text-sm font-semibold text-gray-900">
                              {vehicle.name}
                            </p>
                          </div>

                          <p className="mt-0.5 truncate text-xs text-gray-500">
                            ID: {vehicleId}
                            {vehicle.description
                              ? ` · ${vehicle.description}`
                              : " · No description"}
                          </p>

                          {/* CURRENT FARE */}

                          {currentFare && (
                            <p className="mt-1 text-xs text-gray-500">
                              Current slab:{" "}
                              <span className="font-medium text-gray-800">
                                {currentFare.minimumDistance ??
                                  currentFare.minimum_distance ??
                                  0}
                                km -{" "}
                                {currentFare.maximumDistance ??
                                  currentFare.maximum_distance ??
                                  0}
                                km
                              </span>{" "}
                              · Fare{" "}
                              <span className="font-medium text-gray-800">
                                ₹{formatAmount(currentFare.fare)}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* =================================================
                          FARE INPUTS
                      ================================================= */}

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        {/* MINIMUM DISTANCE */}

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Minimum Distance
                          </label>

                          <input
                            type="number"
                            min="0"
                            value={form.minimumDistance || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicleId,
                                "minimumDistance",
                                e.target.value,
                              )
                            }
                            placeholder="0"
                            className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                          />
                        </div>

                        {/* MAXIMUM DISTANCE */}

                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Maximum Distance
                          </label>

                          <input
                            type="number"
                            min="0"
                            value={form.maximumDistance || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicleId,
                                "maximumDistance",
                                e.target.value,
                              )
                            }
                            placeholder="2"
                            className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                          />
                        </div>

                        {/* BASE FARE */}

                        <FareInput
                          label="Base Fare"
                          value={form.fare || ""}
                          placeholder="20.4"
                          onChange={(value) =>
                            handleFareInput(vehicleId, "fare", value)
                          }
                        />

                        {/* RAINY */}

                        <FareInput
                          label="Rainy Weather"
                          value={form.rainyWeather || ""}
                          placeholder="41.2"
                          onChange={(value) =>
                            handleFareInput(vehicleId, "rainyWeather", value)
                          }
                        />

                        {/* NIGHT */}

                        <FareInput
                          label="Night Charge"
                          value={form.nightCharge || ""}
                          placeholder="78.25"
                          onChange={(value) =>
                            handleFareInput(vehicleId, "nightCharge", value)
                          }
                        />

                        {/* SURCHARGE */}

                        <FareInput
                          label="Surcharge"
                          value={form.surcharge || ""}
                          placeholder="20"
                          onChange={(value) =>
                            handleFareInput(vehicleId, "surcharge", value)
                          }
                        />

                        {/* ADDITIONAL */}

                        <FareInput
                          label="Additional Charge"
                          value={form.additionalCharge || ""}
                          placeholder="36"
                          onChange={(value) =>
                            handleFareInput(
                              vehicleId,
                              "additionalCharge",
                              value,
                            )
                          }
                        />
                      </div>

                      {/* =================================================
                          ACTIONS
                      ================================================= */}

                      <div className="flex items-end gap-2 md:justify-end">
                        {/* SAVE */}

                        <button
                          type="button"
                          onClick={() => handleSaveFare(vehicle)}
                          disabled={
                            isFareSaving || !form.maximumDistance || !form.fare
                          }
                          className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-orange-500 px-3 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 md:flex-none"
                        >
                          {isFareSaving ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Save size={14} />
                          )}
                          Save
                        </button>

                        {/* DELETE VEHICLE */}

                        <button
                          type="button"
                          onClick={() => handleDeleteVehicle(vehicle)}
                          disabled={isDeleting}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-60"
                          title="Delete vehicle"
                        >
                          {isDeleting ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* =================================================
                        EXISTING DISTANCE SLABS
                    ================================================= */}

                    {isExpanded && (
                      <div className="mx-4 mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                        {/* HEADER */}

                        <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-900">
                              Existing Distance Slabs
                            </p>

                            <p className="text-[11px] text-gray-500">
                              {slabs.length} fare rules configured for this
                              vehicle
                            </p>
                          </div>

                          <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                            {slabs.length} slabs
                          </span>
                        </div>

                        {/* EMPTY */}

                        {slabs.length === 0 ? (
                          <div className="px-3 py-4 text-xs text-gray-500">
                            No distance slab added yet.
                          </div>
                        ) : (
                          /* =============================================
                              SLABS TABLE
                          ============================================= */

                          <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-xs">
                              <thead className="bg-gray-50 text-gray-500">
                                <tr>
                                  <th className="px-3 py-2 font-medium">
                                    Distance
                                  </th>

                                  <th className="px-3 py-2 font-medium">
                                    Fare
                                  </th>

                                  <th className="px-3 py-2 font-medium">
                                    Rainy
                                  </th>

                                  <th className="px-3 py-2 font-medium">
                                    Night
                                  </th>

                                  <th className="px-3 py-2 font-medium">
                                    Surcharge
                                  </th>

                                  <th className="px-3 py-2 font-medium">
                                    Additional
                                  </th>

                                  <th className="px-3 py-2 text-right font-medium">
                                    Action
                                  </th>
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-gray-100">
                                {slabs.map((slab) => {
                                  const isEditing =
                                    String(form.pkDistanceFareId || "") ===
                                    String(slab.pk_distance_fare_id || "");

                                  const isDeletingSlab =
                                    String(deletingDistanceFareId || "") ===
                                    String(slab.pk_distance_fare_id || "");

                                  return (
                                    <tr
                                      key={slab.pk_distance_fare_id}
                                      className={`transition hover:bg-gray-50 ${
                                        isEditing
                                          ? "bg-orange-50/70"
                                          : "bg-white"
                                      }`}
                                    >
                                      {/* DISTANCE */}

                                      <td className="px-3 py-2 font-medium text-gray-900">
                                        {formatAmount(slab.minimum_distance)} km
                                        - {formatAmount(slab.maximum_distance)}{" "}
                                        km
                                      </td>

                                      {/* FARE */}

                                      <td className="px-3 py-2 text-gray-700">
                                        ₹{formatAmount(slab.fare)}
                                      </td>

                                      {/* RAINY */}

                                      <td className="px-3 py-2 text-gray-700">
                                        ₹
                                        {formatAmount(
                                          slab.rainy_weather_charge,
                                        )}
                                      </td>

                                      {/* NIGHT */}

                                      <td className="px-3 py-2 text-gray-700">
                                        ₹{formatAmount(slab.night_charge)}
                                      </td>

                                      {/* SURCHARGE */}

                                      <td className="px-3 py-2 text-gray-700">
                                        ₹{formatAmount(slab.surcharge)}
                                      </td>

                                      {/* ADDITIONAL */}

                                      <td className="px-3 py-2 text-gray-700">
                                        ₹{formatAmount(slab.additional_charge)}
                                      </td>

                                      {/* ACTION */}

                                      <td className="px-3 py-2 text-right">
                                        <div className="flex justify-end gap-2">
                                          {/* EDIT */}

                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleEditDistanceSlab(
                                                vehicleId,
                                                slab,
                                              )
                                            }
                                            className="rounded-md border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100"
                                          >
                                            {isEditing ? "Editing" : "Edit"}
                                          </button>

                                          {/* DELETE */}

                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleDeleteDistanceFare(
                                                vehicle,
                                                slab,
                                              )
                                            }
                                            disabled={isDeletingSlab}
                                            className="rounded-md border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                                          >
                                            {isDeletingSlab
                                              ? "Deleting"
                                              : "Delete"}
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// =====================================================
// COMMON RUPEE INPUT
// =====================================================

const FareInput = ({ label, value, onChange, placeholder }) => {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">
        {label}
      </label>

      <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-2 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400">
        <IndianRupee size={13} className="text-gray-400" />

        <input
          type="number"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="ml-1 w-full bg-transparent text-sm outline-none"
        />
      </div>
    </div>
  );
};

export default VehicleFareList;
